import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';

import { useNotifications } from '../../contexts/NotificationContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTenantData } from '../../contexts/TenantDataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useIntercom } from '../shared/IntercomProvider';
import { billingManager } from '../../business/billing/BillingManager';
import { openCommunityGroup } from '../../constants/externalLinks';
import { ReferAndEarnModal } from '../referral';
import {
  Home,
  MessageSquare,
  Users,
  Bell,
  FileText,
  Settings,
  BarChart3,

  DollarSign,
  ChevronRight,
  BookOpen,
  Zap,
  PanelLeftClose,
  PanelLeft,
  Sparkles,
  Crown,
  ArrowUpRight,
  GraduationCap,
  Mic,
  Lock,
  LayoutTemplate,
  CalendarDays,
  LifeBuoy,
  UsersRound,
} from 'lucide-react';
interface SidebarProps {
  className?: string;
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  isCollapsed?: boolean;
  /** Muted styling — item remains visible and navigable (page shows upgrade gate) */
  locked?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  badge,
  isCollapsed = false,
  locked = false,
}) => {
  const location = useLocation();
  const path = to.split('?')[0];
  const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const itemRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (isCollapsed && itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 12
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const itemClassName = `
    relative flex items-center gap-3 px-3 py-2.5 rounded-xl w-full
    font-medium text-13 tracking-wide
    transition-all duration-300 ease-out
    ${isCollapsed ? 'justify-center' : ''}
    ${locked
      ? 'text-white/40 hover:text-white/50 hover:bg-surface/[0.04] cursor-pointer'
      : isActive
        ? 'bg-surface/[0.12] text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)]'
        : 'text-white/70 hover:text-white hover:bg-surface/[0.06]'
    }
  `;

  return (
    <div 
      ref={itemRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link
        to={to}
        className={itemClassName}
        title={locked ? 'Requires Scale plan' : undefined}
      >
        {!locked && isActive && (
          <span
            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-gradient-to-b from-blue-400 to-blue-500 rounded-r-full shadow-[0_0_12px_rgba(59,130,246,0.5)]"
          />
        )}
        <span
          className={`relative flex items-center justify-center w-5 h-5 transition-transform duration-200 ${!locked && isActive ? 'scale-110' : 'group-hover:scale-105'}`}
        >
          {icon}
          {isCollapsed && !locked && badge !== undefined && badge > 0 && (
            <span className="absolute -top-1.5 -right-1.5 bg-gradient-to-br from-rose-500 to-pink-600 text-white text-9 font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center shadow-lg ring-2 ring-slate-900">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
        {!isCollapsed && (
          <>
            <span className="flex-1 truncate">{label}</span>
            {locked && <Lock size={14} className="text-white/35 flex-shrink-0" aria-hidden />}
            {!locked && badge !== undefined && badge > 0 && (
              <span className="ml-auto bg-gradient-to-br from-rose-500 to-pink-600 text-white text-2xs font-bold px-2 py-0.5 rounded-full shadow-lg">
                {badge}
              </span>
            )}
          </>
        )}
      </Link>
      
      {/* Tooltip for collapsed mode - rendered via portal to escape overflow */}
      {isCollapsed && showTooltip && createPortal(
        <div 
          className="fixed z-[9999] pointer-events-none"
          style={{ 
            top: tooltipPosition.top, 
            left: tooltipPosition.left,
            transform: 'translateY(-50%)'
          }}
        >
          <div className="relative bg-slate-800/95 backdrop-blur-xl text-white text-xs font-medium px-3 py-2 rounded-lg shadow-xl border border-white/10">
            <span className="whitespace-nowrap">{label}</span>
            {badge !== undefined && badge > 0 && (
              <span className="ml-2 text-rose-400">({badge})</span>
            )}
            {/* Arrow */}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-slate-800/95" />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

interface SidebarActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isCollapsed?: boolean;
}

/**
 * A sidebar row that fires an action instead of navigating (e.g. "Help" which
 * opens the Intercom messenger). Mirrors `NavItem`'s look — including the
 * collapsed-mode hover tooltip rendered via portal — but is a real <button>
 * with no active/route state.
 */
const SidebarActionButton: React.FC<SidebarActionButtonProps> = ({
  icon,
  label,
  onClick,
  isCollapsed = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const itemRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (isCollapsed && itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      setTooltipPosition({ top: rect.top + rect.height / 2, left: rect.right + 12 });
      setShowTooltip(true);
    }
  };

  return (
    <div
      ref={itemRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={onClick}
        title={isCollapsed ? label : undefined}
        className={`
          relative flex items-center gap-3 px-3 py-2.5 rounded-xl w-full
          font-medium text-13 tracking-wide
          transition-all duration-300 ease-out
          text-white/70 hover:text-white hover:bg-surface/[0.06]
          ${isCollapsed ? 'justify-center' : ''}
        `}
      >
        <span className="relative flex items-center justify-center w-5 h-5 transition-transform duration-200 group-hover:scale-105">
          {icon}
        </span>
        {!isCollapsed && <span className="flex-1 truncate text-left">{label}</span>}
      </button>

      {isCollapsed && showTooltip && createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left, transform: 'translateY(-50%)' }}
        >
          <div className="relative bg-slate-800/95 backdrop-blur-xl text-white text-xs font-medium px-3 py-2 rounded-lg shadow-xl border border-white/10">
            <span className="whitespace-nowrap">{label}</span>
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-slate-800/95" />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

interface PlanInfo {
  name: string;
  tier: string;
  icon: React.ReactNode;
  gradient: string;
}

const getPlanInfo = (tier: string | number | undefined): PlanInfo => {
  if (typeof tier === 'string') {
    const tierLower = tier.toLowerCase();
    if (tierLower === 'scale' || tierLower === 'premium') {
      return { name: 'Scale', tier: 'scale', icon: <Crown size={14} />, gradient: 'from-emerald-500 to-emerald-600' };
    }
    if (tierLower === 'pro' || tierLower === 'growth') {
      return { name: 'Pro', tier: 'pro', icon: <Sparkles size={14} />, gradient: 'from-emerald-500 to-emerald-600' };
    }
    return { name: 'Starter', tier: 'solo', icon: <Zap size={14} />, gradient: 'from-amber-500 to-orange-600' };
  }
  if (typeof tier === 'number') {
    if (tier === 2) return { name: 'Scale', tier: 'scale', icon: <Crown size={14} />, gradient: 'from-emerald-500 to-emerald-600' };
    if (tier === 1) return { name: 'Pro', tier: 'pro', icon: <Sparkles size={14} />, gradient: 'from-emerald-500 to-emerald-600' };
    return { name: 'Starter', tier: 'solo', icon: <Zap size={14} />, gradient: 'from-amber-500 to-orange-600' };
  }
  return { name: 'Starter', tier: 'solo', icon: <Zap size={14} />, gradient: 'from-amber-500 to-orange-600' };
};

const Sidebar: React.FC<SidebarProps> = ({ className }) => {

  const { unreadCount, requestsBadgeCount, notificationsBadgeCount, bookingsBadgeCount } = useNotifications();
  const { isCollapsed, toggleCollapse } = useSidebar();
  const { branding: { businessName } } = useTenantData();
  // Only show the company name once real branding has loaded (the resolver
  // defaults to 'Your Business' before settings arrive).
  const showCompanyName = !!businessName && businessName !== 'Your Business';
  const { canManageRequests, canEditBookingForms, canEditCompanySettings, canViewBilling, canAccessVoiceAgent } = usePermissions();
  const { openIntercom } = useIntercom();
  const { available: hasVoiceAgent, loading: voiceAgentLoading } = useFeatureAccess('VoiceAgent');
  // Voice Agent menu item visible to Super Users and Scheduling Assistants
  // on a Scale-tier plan. Wait out the feature-access load so we don't
  // briefly flash the item to users who don't have it.
  const showVoiceAgent = canAccessVoiceAgent && !voiceAgentLoading && hasVoiceAgent;
  const [currentPlan, setCurrentPlan] = useState<PlanInfo | null>(null);
  const [isInTrial, setIsInTrial] = useState(false);
  const [referModalOpen, setReferModalOpen] = useState(false);
  const [planHover, setPlanHover] = useState(false);
  const [planPosition, setPlanPosition] = useState({ top: 0, left: 0 });
  const planRef = useRef<HTMLDivElement>(null);

  // Fetch current subscription plan (only for users who can view billing)
  useEffect(() => {
    if (!canViewBilling) return;
    const loadPlan = async () => {
      try {
        const summary = await billingManager.getSubscriptionSummary();
        if (summary.noSubscriptionExists) {
          setCurrentPlan({ name: 'No Plan', tier: 'none', icon: <Zap size={14} />, gradient: 'from-slate-400 to-slate-500' });
          setIsInTrial(false);
        } else {
          const planInfo = getPlanInfo(summary.subscription?.currentTier);
          setCurrentPlan(planInfo);
          setIsInTrial(summary.subscription?.status === 'InTrial');
        }
      } catch (error) {
        console.error('Failed to load subscription info:', error);
        setCurrentPlan({ name: 'Starter', tier: 'solo', icon: <Zap size={14} />, gradient: 'from-amber-500 to-orange-600' });
        setIsInTrial(false);
      }
    };
    loadPlan();
  }, [canViewBilling]);

  const handlePlanMouseEnter = () => {
    if (isCollapsed && planRef.current) {
      const rect = planRef.current.getBoundingClientRect();
      setPlanPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 12
      });
      setPlanHover(true);
    }
  };


  return (
    <aside 
      className={`
        ${isCollapsed ? 'w-[72px]' : 'w-64'} 
        relative flex flex-col
        bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950
        transition-all duration-300 ease-out
        hidden md:flex
        ${className}
      `}
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.03] via-transparent to-blue-500/[0.03] pointer-events-none" />
      
      {/* Right border with subtle glow */}
      <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-white/[0.08] via-white/[0.04] to-transparent" />
      
      {/* Logo Header */}
      <div className={`
        relative flex items-center h-16
        ${isCollapsed ? 'justify-center' : 'justify-between px-4'} 
      `}>
        {/* Logo */}
        <Link to="/bookings" className={`flex items-center ${isCollapsed ? '' : 'flex-1 min-w-0'}`}>
          <img 
            src={isCollapsed 
              ? "https://routemize-public-web.s3.us-east-1.amazonaws.com/icon.png"
              : "https://routemize-public-web.s3.us-east-1.amazonaws.com/new-logo.png"
            }
            alt="Routemize" 
            className={`object-contain transition-all duration-300 ${isCollapsed ? 'w-9 h-9 rounded-lg' : 'w-full max-w-[144px] h-auto'}`}
          />
        </Link>
        
        {/* Toggle Button */}
        {!isCollapsed && (
          <button
            onClick={toggleCollapse}
            className="
              flex-shrink-0 p-2 rounded-lg
              text-white/50 hover:text-white hover:bg-surface/10
              transition-all duration-200
            "
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <PanelLeftClose size={18} />
          </button>
        )}
      </div>
      
      {/* Company name under the logo */}
      {!isCollapsed && showCompanyName && (
        <div className="px-4 -mt-3 mb-1">
          <span
            className="block text-xs font-medium tracking-wide text-white/55 truncate"
            title={businessName}
          >
            {businessName}
          </span>
        </div>
      )}

      {/* Expand button when collapsed */}
      {isCollapsed && (
        <div className="flex justify-center pb-2">
          <button
            onClick={toggleCollapse}
            className="
              p-2 rounded-lg
              text-white/50 hover:text-white hover:bg-surface/10
              transition-all duration-200
            "
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <PanelLeft size={18} />
          </button>
        </div>
      )}
      
      {/* Divider */}
      <div className="mx-3 mb-3 h-px bg-white/10" />
      
      {/* Navigation Container */}
      <div className="flex-1 flex flex-col px-3 pb-4 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
        
        {/* Main Navigation */}
        <nav className="flex-1 space-y-1">
          <NavItem 
            to="/dashboard" 
            icon={<Home size={18} strokeWidth={2} />} 
            label="Dashboard" 
            isCollapsed={isCollapsed} 
          />
          {canManageRequests && (
            <NavItem
              to="/requests"
              icon={<MessageSquare size={18} strokeWidth={2} />}
              label="Requests"
              badge={requestsBadgeCount}
              isCollapsed={isCollapsed}
            />
          )}
          <NavItem
            to="/bookings"
            icon={<BookOpen size={18} strokeWidth={2} />}
            label="Bookings"
            badge={bookingsBadgeCount}
            isCollapsed={isCollapsed}
          />
          <NavItem 
            to="/contacts" 
            icon={<Users size={18} strokeWidth={2} />} 
            label="Contacts" 
            isCollapsed={isCollapsed} 
          />
          {/* <NavItem
            to="/notifications"
            icon={<Bell size={18} strokeWidth={2} />}
            label="Notifications"
            badge={notificationsBadgeCount}
            isCollapsed={isCollapsed}
          /> */}
          {canEditBookingForms && (
            <NavItem
              to="/booking-forms"
              icon={<FileText size={18} strokeWidth={2} />}
              label="Booking Forms"
              isCollapsed={isCollapsed}
            />
          )}
          {canEditBookingForms && (
            <NavItem
              to="/landing-pages"
              icon={<LayoutTemplate size={18} strokeWidth={2} />}
              label="Landing Pages"
              isCollapsed={isCollapsed}
            />
          )}
          {showVoiceAgent && (
            <NavItem
              to="/voice-agent"
              icon={<Mic size={18} strokeWidth={2} />}
              label="Voice Agent"
              isCollapsed={isCollapsed}
            />
          )}
          <NavItem
            to="/tutorials"
            icon={<GraduationCap size={18} strokeWidth={2} />}
            label="Tutorials"
            isCollapsed={isCollapsed}
          />

        </nav>

        {/* Analytics & Settings - separated from main nav */}
        <div className="mx-0 my-2 h-px bg-white/10" />
        <NavItem
          to="/analytics"
          icon={<BarChart3 size={18} strokeWidth={2} />}
          label="Analytics"
          isCollapsed={isCollapsed}
        />
        {canEditCompanySettings && (
          <NavItem
            to="/settings"
            icon={<Settings size={18} strokeWidth={2} />}
            label="Settings"
            isCollapsed={isCollapsed}
          />
        )}

        {/* Availability — opens the current user's 30-day schedule with the availability view pre-selected */}
        <NavItem
          to="/calendar?view=availability"
          icon={<CalendarDays size={18} strokeWidth={2} />}
          label="Availability"
          isCollapsed={isCollapsed}
        />

        {/* Help — opens the Intercom messenger. Lives here because Intercom's
            own floating launcher is hidden app-wide (see IntercomProvider) so
            the bottom-right corner is reserved for the Routy launcher. */}
        <SidebarActionButton
          icon={<LifeBuoy size={18} strokeWidth={2} />}
          label="Help"
          onClick={openIntercom}
          isCollapsed={isCollapsed}
        />

        {/* Community — peer group chat on Facebook Messenger, kept separate
            from Help so official support stays inside Intercom. */}
        <SidebarActionButton
          icon={<UsersRound size={18} strokeWidth={2} />}
          label="Community"
          onClick={openCommunityGroup}
          isCollapsed={isCollapsed}
        />

        {/* Current Plan & Usage Indicator - Expanded */}
        {currentPlan && !isCollapsed && canViewBilling && (
          <Link
            to="/billing"
            className="relative mt-4 mb-3 p-4 rounded-xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/[0.08] backdrop-blur-sm overflow-hidden group hover:border-white/[0.15] transition-all duration-300"
          >
            {/* Decorative glow based on plan */}
            <div className={`absolute -top-10 -right-10 w-24 h-24 bg-gradient-to-br ${currentPlan.gradient} opacity-20 rounded-full blur-2xl group-hover:opacity-30 transition-opacity`} />
            
            <div className="relative">
              {/* Plan Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-br ${currentPlan.gradient} text-white`}>
                    {currentPlan.icon}
                  </span>
                  <div>
                    <span className="text-2xs uppercase tracking-wider text-white/50 block">Current Plan</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-white">{currentPlan.name}</span>
                      {isInTrial && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-2xs font-semibold">
                          Free trial
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <ArrowUpRight size={16} className="text-white/40 group-hover:text-white/70 transition-colors" />
              </div>
              
              {/* Upgrade CTA for non-Scale plans */}
              {currentPlan.tier !== 'scale' && (
                <div className="mt-3 pt-3 border-t border-white/[0.06]">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/60">Unlock more</span>
                    <span className="text-xs font-medium text-emerald-400 group-hover:text-emerald-300 transition-colors flex items-center gap-1">
                      View Plans
                      <ChevronRight size={12} className="transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Link>
        )}

        {/* Current Plan Indicator - Collapsed */}
        {currentPlan && isCollapsed && canViewBilling && (
          <div 
            ref={planRef}
            className="relative mb-3"
            onMouseEnter={handlePlanMouseEnter}
            onMouseLeave={() => setPlanHover(false)}
          >
            <Link
              to="/billing"
              className={`
                flex items-center justify-center p-2.5
                rounded-xl
                bg-gradient-to-br ${currentPlan.gradient}
                text-white
                transition-all duration-200
                hover:scale-105
                relative overflow-hidden
              `}
            >
              {currentPlan.icon}
            </Link>
            {/* Tooltip via portal */}
            {planHover && createPortal(
              <div 
                className="fixed z-[9999] pointer-events-none"
                style={{ 
                  top: planPosition.top, 
                  left: planPosition.left,
                  transform: 'translateY(-50%)'
                }}
              >
                <div className="relative bg-slate-800/95 backdrop-blur-xl text-white text-xs font-medium px-3 py-2 rounded-lg shadow-xl border border-white/10">
                  <span className="whitespace-nowrap">{currentPlan.name}</span>
                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-slate-800/95" />
                </div>
              </div>,
              document.body
            )}
          </div>
        )}

        {/* Refer & Earn — opens a modal rather than navigating */}
        <SidebarActionButton
          icon={<DollarSign size={18} strokeWidth={2} />}
          label="Refer & Earn $50"
          onClick={() => setReferModalOpen(true)}
          isCollapsed={isCollapsed}
        />

      </div>

      <ReferAndEarnModal isOpen={referModalOpen} onClose={() => setReferModalOpen(false)} />
    </aside>
  );
};

export default Sidebar;
